/**
 * Resolving "which cameras have model X attached" from the node's assignments payload.
 *
 * Both dashboards read the same node endpoint (`/models/assignments/all`), and a camera can get
 * a model TWO ways — attached directly, or inherited from a group it belongs to — so the answer
 * is a reduction over four lists rather than a field on the camera. The camera list itself
 * carries no models at all, which is why this exists.
 *
 * Kept here rather than in each app's adapter so the two dashboards can't drift on a question
 * whose answer decides what an operator is told about a silent camera.
 */

/** The `/models/assignments/all` payload, as much of it as this needs. */
export interface ModelAssignmentsPayload {
  models?: Array<{ id?: string; name?: string }>;
  camera_assignments?: Array<{ camera_id?: string; model_id?: string; name?: string }>;
  group_assignments?: Array<{ group_id?: string; model_id?: string }>;
  camera_group_memberships?: Array<{ camera_id?: string; group_id?: string }>;
  [key: string]: unknown;
}

/**
 * Camera ids that have a model whose name contains `modelName` (case-insensitive), counting both
 * direct and group-inherited attachments.
 *
 * Substring rather than equality: deployments name the model variously ("heatmap",
 * "heatmap_v2", "Heatmap Detection"), and the live-view badge that opens the heatmap already
 * matches the same way — the two should agree about which cameras have it.
 */
export function cameraIdsForModel(
  payload: ModelAssignmentsPayload | null | undefined,
  modelName: string,
): string[] {
  if (!payload) return [];
  const needle = modelName.toLowerCase();

  const matchingModelIds = new Set(
    (payload.models || [])
      .filter((m) => String(m?.name || '').toLowerCase().includes(needle))
      .map((m) => String(m?.id || '')),
  );

  const cameraIds = new Set<string>();

  // Direct attachments. `name` is trusted when present — a camera assignment can name the model
  // even for an id missing from the `models` list (a model deleted since it was attached).
  for (const a of payload.camera_assignments || []) {
    const named = String(a?.name || '').toLowerCase().includes(needle);
    if ((named || matchingModelIds.has(String(a?.model_id || ''))) && a?.camera_id) {
      cameraIds.add(String(a.camera_id));
    }
  }

  // Group attachments, expanded through group membership.
  const matchingGroups = new Set(
    (payload.group_assignments || [])
      .filter((a) => matchingModelIds.has(String(a?.model_id || '')))
      .map((a) => String(a?.group_id || '')),
  );
  if (matchingGroups.size) {
    for (const m of payload.camera_group_memberships || []) {
      if (m?.camera_id && matchingGroups.has(String(m?.group_id || ''))) {
        cameraIds.add(String(m.camera_id));
      }
    }
  }

  return [...cameraIds];
}
